import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';

import { insideNativeApp } from '../../auth/native-app';
import { isCoach, isOwner } from '../../auth/roles';
import { usePermissions } from '../../auth/usePermissions';
import { useSession } from '../../auth/useSession';
import { canSearchClients } from '../../lib/clientSearch';
import { planLabel } from '../../lib/plan';
import { setTema, useTema } from '../../lib/tema';
import { centerOf, withViewTransition } from '../../lib/motion/viewTransition';
import { useBusiness } from '../../lib/useBusiness';
import { whoAmI } from '../../lib/whoami';
import { AccountMenu } from '../AccountMenu';
import { LocationSwitcher } from '../NavBar';
import type { NavModel } from './navModel';
import { useLocations } from './useLocations';

const MONTH_FORMAT = new Intl.DateTimeFormat('es-PA', { month: 'long', year: 'numeric' });

function thisMonth(): string {
  const label = MONTH_FORMAT.format(new Date());
  return label.charAt(0).toUpperCase() + label.slice(1);
}

function monogram(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  const letters = words.length > 1 ? `${words[0]![0]}${words[1]![0]}` : name.slice(0, 2);
  return letters.toUpperCase();
}

function Sede({ owner }: { owner: boolean }) {
  const business = useBusiness();
  const { locations, switchMutation } = useLocations(owner);
  if (owner && locations.length > 1) {
    return (
      <span className="hs-bar-btn hs-sede">
        <PlaceGlyph />
        <LocationSwitcher locations={locations} onSwitch={(id) => switchMutation.mutate(id)} pending={switchMutation.isPending} />
      </span>
    );
  }
  return (
    <span className="hs-bar-btn hs-sede">
      <PlaceGlyph />
      {business?.neighborhood ?? 'Sede principal'}
    </span>
  );
}

function Search() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  function handleSearch(event: FormEvent) {
    event.preventDefault();
    const buscar = query.trim();
    void navigate({ to: '/clientes', search: buscar ? { buscar } : {} });
  }
  return (
    <form className="hs-search" role="search" onSubmit={handleSearch}>
      <SearchGlyph />
      <input type="search" aria-label="Buscar clientes" placeholder="Buscar clientes" value={query} onChange={(event) => setQuery(event.target.value)} />
    </form>
  );
}

/**
 * The glass bar of the Helios CRM in Dale's colors: the business with
 * "powered by Dale.", where and when you are, a client search, Ajustes and
 * the account. Every control reuses something the panel already does.
 */
export function TopBar({ model }: { model: NavModel }) {
  const { session, logout } = useSession();
  const business = useBusiness();
  const permissions = usePermissions();
  const who = whoAmI(session, business, permissions.profile);
  const owner = isOwner(session?.role);
  const canSearch = canSearchClients({ coach: isCoach(session), plan: business?.plan, can: permissions.can });
  const name = business?.name ?? who.name;
  const draft = model.settings.some((link) => link.to === '/primer-dia');
  return (
    <header className="hs-topbar hs-glass">
      <span className="hs-refraction" aria-hidden="true" />
      <Link to="/" className="hs-brand" aria-label={`${name}, inicio`}>
        <span className="hs-monogram" aria-hidden="true">{monogram(name)}</span>
        <span className="hs-brand-text">
          <strong>{name}</strong>
          <span className="hs-powered">
            powered by <span className="hs-powered-word">Dale<span className="dot">.</span></span>
          </span>
        </span>
      </Link>
      <span className="hs-sep" aria-hidden="true" />
      <Sede owner={owner} />
      <span className="hs-bar-btn hs-month">
        <DayGlyph />
        {thisMonth()}
      </span>
      <span className="hs-space" />
      {canSearch ? <Search /> : null}
      {draft ? (
        <Link to="/primer-dia" className="hs-chip">
          Preparar el negocio
        </Link>
      ) : null}
      <TemaButton />
      {model.settings.length > 0 ? (
        <Link to="/ajustes" className="hs-icon-btn" aria-label="Ajustes" activeProps={{ 'data-status': 'active' }}>
          <GearGlyph />
        </Link>
      ) : null}
      <AccountMenu
        placement="down"
        name={who.name}
        roleWord={who.roleWord}
        initials={who.initials}
        {...(owner && business && !insideNativeApp() ? { planLabel: planLabel(business.plan ?? 'base') } : {})}
        onLogout={() => void logout()}
      />
    </header>
  );
}

/** Día / Noche, the Helios moon button. */
function TemaButton() {
  const tema = useTema();
  const night = tema === 'noche';
  return (
    <button
      type="button"
      className="hs-icon-btn"
      aria-label={night ? 'Cambiar a Día' : 'Cambiar a Noche'}
      aria-pressed={night}
      onClick={(event) => withViewTransition(() => setTema(night ? 'dia' : 'noche'), centerOf(event.currentTarget))}
    >
      {night ? <SunGlyph /> : <MoonGlyph />}
    </button>
  );
}

function MoonGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
      <path d="M19.5 14.5A7.5 7.5 0 0 1 9.5 4.5a7.5 7.5 0 1 0 10 10z" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
    </svg>
  );
}

function SunGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
      <circle cx="12" cy="12" r="4" fill="none" stroke="currentColor" strokeWidth="1.7" />
      <path d="M12 2.5v2M12 19.5v2M2.5 12h2M19.5 12h2M5.3 5.3l1.4 1.4M17.3 17.3l1.4 1.4M5.3 18.7l1.4-1.4M17.3 6.7l1.4-1.4" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  );
}

function PlaceGlyph() {
  return (
    <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
      <path d="M4 17V8l6-4 6 4v9M8 17v-5h4v5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function DayGlyph() {
  return (
    <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
      <rect x="3.5" y="4.5" width="13" height="12" rx="2" fill="none" stroke="currentColor" strokeWidth="1.6" />
      <path d="M3.5 8.5h13M7 3v3M13 3v3" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function SearchGlyph() {
  return (
    <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
      <circle cx="9" cy="9" r="5.5" fill="none" stroke="currentColor" strokeWidth="1.6" />
      <path d="m13.2 13.2 3.3 3.3" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function GearGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
      <circle cx="12" cy="12" r="3" fill="none" stroke="currentColor" strokeWidth="1.7" />
      <path
        d="M19.4 13.5a7.6 7.6 0 0 0 0-3l2-1.5-2-3.4-2.3.9a7.5 7.5 0 0 0-2.6-1.5L14 2.5h-4l-.5 2.5a7.5 7.5 0 0 0-2.6 1.5l-2.3-.9-2 3.4 2 1.5a7.6 7.6 0 0 0 0 3l-2 1.5 2 3.4 2.3-.9a7.5 7.5 0 0 0 2.6 1.5l.5 2.5h4l.5-2.5a7.5 7.5 0 0 0 2.6-1.5l2.3.9 2-3.4z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  );
}
